import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";
import { extractInvitedReferralEmails, extractReferralType } from "@/utils/referralHelpers";
import { useMemo } from "react";

/**
 * Datos del embudo de referidos
 */
export interface ReferralFunnelData {
  // Métricas globales
  totalMainSubmissions: number;
  totalInvited: number;
  totalResponded: number;
  conversionRate: number;

  // Por lanzamiento
  byLaunch: ReferralByLaunch[];

  // Detalle por referido
  details: ReferralDetail[];

  // Estado de carga
  isLoading: boolean;
  error: Error | null;
}

export interface ReferralByLaunch {
  launchId: number;
  launchName: string;
  clientName: string;
  invited: number;
  responded: number;
  pending: number;
  conversionRate: number;
}

export interface ReferralDetail {
  inviterSubmissionId: number;
  inviterName: string;
  inviterEmail: string;
  inviterCode: string | null;
  launchId: number | null;
  launchName: string;
  referralEmail: string;
  status: "responded" | "pending";
  respondedAt: string | null;
  referralType: string | null;
  referralSubmissionId: number | null;
}

interface MainSubmission {
  id: number;
  email: string | null;
  code: string | null;
  raw_answers: Record<string, any>;
  jotform_created_at: string;
}

interface ReferralSubmission {
  id: number;
  email: string | null;
  parent_submission_id: number | null;
  jotform_created_at: string;
  raw_answers: Record<string, any>;
}

interface FormCode {
  code: string;
  form_launch_id: number;
}

interface FormLaunch {
  id: number;
  clientes: {
    nombre: string | null;
  } | null;
}

interface UseReferralFunnelOptions {
  dateFrom?: string;
  dateTo?: string;
  launchId?: number;
  enabled?: boolean;
}

/**
 * Hook para obtener datos del embudo de referidos
 *
 * Fuentes de datos:
 * 1. Referidos Invitados: Campo 131 (widget "refe") en raw_answers del formulario principal
 * 2. Referidos que Respondieron: Tabla jotform_submissions con is_referral=true
 *
 * Relación para obtener lanzamiento:
 * jotform_submissions.code → form_codes.code → form_codes.form_launch_id → form_launches.id
 *
 * @param options - Opciones de filtrado
 */
export function useReferralFunnel(options: UseReferralFunnelOptions = {}) {
  const { dateFrom, dateTo, launchId, enabled = true } = options;
  const { projectId } = useProject();

  // Query 1: Obtener submissions principales con sus emails invitados
  const mainSubmissionsQuery = useQuery({
    queryKey: ["referral-funnel-main", projectId, dateFrom, dateTo],
    queryFn: async () => {
      if (!projectId) return [];

      let query = (supabase as any)
        .from("jotform_submissions")
        .select(`
          id,
          email,
          code,
          raw_answers,
          jotform_created_at
        `)
        .eq("project_id", projectId)
        .eq("is_referral", false)
        .eq("is_deleted_in_jotform", false);

      if (dateFrom) {
        query = query.gte("jotform_created_at", dateFrom);
      }
      if (dateTo) {
        query = query.lte("jotform_created_at", dateTo);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching main submissions:", error);
        throw error;
      }

      return (data || []) as MainSubmission[];
    },
    enabled: enabled && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  // Query 2: Obtener referidos que respondieron
  const referralSubmissionsQuery = useQuery({
    queryKey: ["referral-funnel-referrals", projectId],
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await (supabase as any)
        .from("jotform_submissions")
        .select(`
          id,
          email,
          parent_submission_id,
          jotform_created_at,
          raw_answers
        `)
        .eq("project_id", projectId)
        .eq("is_referral", true)
        .eq("is_deleted_in_jotform", false);

      if (error) {
        console.error("Error fetching referral submissions:", error);
        throw error;
      }

      return (data || []) as ReferralSubmission[];
    },
    enabled: enabled && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  // Query 3: Obtener form_codes para mapear código → lanzamiento
  const formCodesQuery = useQuery({
    queryKey: ["referral-funnel-codes", projectId],
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await (supabase as any)
        .from("form_codes")
        .select("code, form_launch_id")
        .eq("project_id", projectId);

      if (error) {
        console.error("Error fetching form codes:", error);
        throw error;
      }

      return (data || []) as FormCode[];
    },
    enabled: enabled && !!projectId,
    staleTime: 1000 * 60 * 10, // 10 min
  });

  // Query 4: Obtener lanzamientos con nombres de clientes
  const launchesQuery = useQuery({
    queryKey: ["referral-funnel-launches", projectId],
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await (supabase as any)
        .from("form_launches")
        .select(`
          id,
          clientes:id_cliente (nombre)
        `)
        .eq("project_id", projectId);

      if (error) {
        console.error("Error fetching launches:", error);
        throw error;
      }

      return (data || []) as FormLaunch[];
    },
    enabled: enabled && !!projectId,
    staleTime: 1000 * 60 * 10, // 10 min
  });

  // Procesar y cruzar datos
  const processedData = useMemo(() => {
    const mainSubmissions = mainSubmissionsQuery.data || [];
    const referralSubmissions = referralSubmissionsQuery.data || [];
    const formCodes = formCodesQuery.data || [];
    const launches = launchesQuery.data || [];

    // Crear mapa código → form_launch_id
    const codeToLaunchMap = new Map<string, number>();
    formCodes.forEach((fc) => {
      codeToLaunchMap.set(fc.code, fc.form_launch_id);
    });

    // Crear mapa launch_id → launch info
    const launchMap = new Map<number, FormLaunch>();
    launches.forEach((l) => {
      launchMap.set(l.id, l);
    });

    // Crear mapa de referidos que respondieron por email (lowercase)
    const respondedByEmail = new Map<
      string,
      { submissionId: number; respondedAt: string; rawAnswers: Record<string, any> }
    >();
    // También por parent_submission_id para match más preciso
    const respondedByParentId = new Map<number, ReferralSubmission[]>();

    referralSubmissions.forEach((ref) => {
      if (ref.email) {
        respondedByEmail.set(ref.email.toLowerCase(), {
          submissionId: ref.id,
          respondedAt: ref.jotform_created_at,
          rawAnswers: ref.raw_answers,
        });
      }
      if (ref.parent_submission_id) {
        const existing = respondedByParentId.get(ref.parent_submission_id) || [];
        existing.push(ref);
        respondedByParentId.set(ref.parent_submission_id, existing);
      }
    });

    // Procesar cada submission principal y extraer emails invitados
    const details: ReferralDetail[] = [];
    const byLaunchMap = new Map<
      number,
      { launchId: number; launchName: string; clientName: string; invited: number; responded: number }
    >();

    let totalInvited = 0;
    let totalResponded = 0;

    mainSubmissions.forEach((main) => {
      const invitedEmails = extractInvitedReferralEmails(main.raw_answers);
      if (invitedEmails.length === 0) return;

      // Obtener lanzamiento a través del código
      const launchIdFromCode = main.code ? codeToLaunchMap.get(main.code) : null;
      const launch = launchIdFromCode ? launchMap.get(launchIdFromCode) : null;

      const launchIdValue = launchIdFromCode || 0;
      const clientName = launch?.clientes?.nombre || "Sin cliente";
      // Usar cliente como nombre del lanzamiento ya que es más descriptivo
      const launchName = clientName !== "Sin cliente" ? clientName : "Sin lanzamiento";

      // Filtrar por lanzamiento si se especificó
      if (launchId && launchIdValue !== launchId) return;

      // Obtener referidos que respondieron para este padre
      const respondedForParent = respondedByParentId.get(main.id) || [];
      const respondedEmailsForParent = new Set(
        respondedForParent.map((r) => r.email?.toLowerCase()).filter(Boolean)
      );

      // Extraer nombre del invitador de raw_answers
      const inviterName =
        extractNameFromRawAnswers(main.raw_answers) || main.email || "Sin nombre";

      invitedEmails.forEach((email) => {
        const emailLower = email.toLowerCase();
        // Verificar si respondió (por parent_id o por email)
        const respondedByParent = respondedEmailsForParent.has(emailLower);
        const respondedInfo = respondedByEmail.get(emailLower);
        const hasResponded = respondedByParent || !!respondedInfo;

        // Obtener tipo de relación si respondió
        let referralType: string | null = null;
        let respondedAt: string | null = null;
        let referralSubmissionId: number | null = null;

        if (hasResponded) {
          if (respondedByParent) {
            const matchedRef = respondedForParent.find(
              (r) => r.email?.toLowerCase() === emailLower
            );
            if (matchedRef) {
              referralType = extractReferralType(matchedRef.raw_answers);
              respondedAt = matchedRef.jotform_created_at;
              referralSubmissionId = matchedRef.id;
            }
          } else if (respondedInfo) {
            referralType = extractReferralType(respondedInfo.rawAnswers);
            respondedAt = respondedInfo.respondedAt;
            referralSubmissionId = respondedInfo.submissionId;
          }
        }

        details.push({
          inviterSubmissionId: main.id,
          inviterName,
          inviterEmail: main.email || "",
          inviterCode: main.code,
          launchId: launchIdValue,
          launchName,
          referralEmail: email,
          status: hasResponded ? "responded" : "pending",
          respondedAt,
          referralType,
          referralSubmissionId,
        });

        totalInvited++;
        if (hasResponded) totalResponded++;

        // Agregar a métricas por lanzamiento
        if (!byLaunchMap.has(launchIdValue)) {
          byLaunchMap.set(launchIdValue, {
            launchId: launchIdValue,
            launchName,
            clientName,
            invited: 0,
            responded: 0,
          });
        }
        const launchStats = byLaunchMap.get(launchIdValue)!;
        launchStats.invited++;
        if (hasResponded) launchStats.responded++;
      });
    });

    // Convertir mapa a array y calcular tasas de conversión
    const byLaunch: ReferralByLaunch[] = Array.from(byLaunchMap.values()).map(
      (stats) => ({
        ...stats,
        pending: stats.invited - stats.responded,
        conversionRate:
          stats.invited > 0
            ? Math.round((stats.responded / stats.invited) * 1000) / 10
            : 0,
      })
    );

    // Ordenar por cantidad de invitados (desc)
    byLaunch.sort((a, b) => b.invited - a.invited);

    return {
      totalMainSubmissions: mainSubmissions.length,
      totalInvited,
      totalResponded,
      conversionRate:
        totalInvited > 0
          ? Math.round((totalResponded / totalInvited) * 1000) / 10
          : 0,
      byLaunch,
      details,
    };
  }, [
    mainSubmissionsQuery.data,
    referralSubmissionsQuery.data,
    formCodesQuery.data,
    launchesQuery.data,
    launchId,
  ]);

  const isLoading =
    mainSubmissionsQuery.isLoading ||
    referralSubmissionsQuery.isLoading ||
    formCodesQuery.isLoading ||
    launchesQuery.isLoading;

  const error =
    mainSubmissionsQuery.error ||
    referralSubmissionsQuery.error ||
    formCodesQuery.error ||
    launchesQuery.error;

  return {
    ...processedData,
    isLoading,
    error: error as Error | null,
    refetch: () => {
      mainSubmissionsQuery.refetch();
      referralSubmissionsQuery.refetch();
    },
  };
}

/**
 * Extrae el nombre del invitador de los raw_answers
 */
function extractNameFromRawAnswers(
  rawAnswers: Record<string, any>
): string | null {
  if (!rawAnswers) return null;

  // Buscar campos comunes de nombre
  const nameFields = ["3", "nombre", "name", "fullName", "full_name"];

  for (const field of nameFields) {
    const value = rawAnswers[field];
    if (value?.answer && typeof value.answer === "string") {
      return value.answer.trim();
    }
    if (value?.prettyFormat && typeof value.prettyFormat === "string") {
      return value.prettyFormat.trim();
    }
  }

  // Buscar cualquier campo que tenga "nombre" en el text
  for (const key in rawAnswers) {
    const field = rawAnswers[key];
    if (
      field?.text &&
      typeof field.text === "string" &&
      field.text.toLowerCase().includes("nombre") &&
      field.answer
    ) {
      if (typeof field.answer === "string") {
        return field.answer.trim();
      }
      if (typeof field.answer === "object" && field.prettyFormat) {
        return field.prettyFormat.trim();
      }
    }
  }

  return null;
}
